import React, { useCallback } from "react";
import { AlertTriangle } from "lucide-react";
import { FunnelCardHeader } from "./FunnelCardHeader";
import { FunnelCardBody } from "./FunnelCardBody";
import { FunnelCardFooter } from "./FunnelCardFooter";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { selectContractFunnelById } from "../../slices/pricing/selectors";
import { XMarkIcon } from "components/icons/Icons";
import { deleteUpdatedContractFunnel } from "../../slices/pricing/pricingSlice";
import { FunnelCardWarning } from "components/display/FunnelCardWarning";

type Props = {
  index: number,
  isProMode: boolean,
  contractFunnelId: string,
  contractIdentifier?: string,
};


export const FunnelCard: React.FC<Props> = ({
  index,
  isProMode,
  contractFunnelId,
  contractIdentifier,
}) => {
  const dispatch = useAppDispatch()

  const { contractFunnel, isEditMode } = useAppSelector(selectContractFunnelById(contractFunnelId, contractIdentifier))

  const killEditMode = useCallback(() => {
    if (contractFunnel && contractFunnel.hash)
      dispatch(deleteUpdatedContractFunnel(contractFunnel.hash))
  }, [contractFunnel, dispatch])

  if (!contractFunnel) {
    return <></>
  }

  // Produit inéligible / non tarifé (isSuccess=false) : carte grisée. Le footer porte
  // le message et désactive l'ajout ; on grise seulement les visuels produit (logo,
  // garanties…) pour garder le message d'inéligibilité bien lisible.
  const isIneligible = contractFunnel.isSuccess === false

  return (
    <div
      key={index}
      className={`relative rounded-xl ${isEditMode ? 'border-2 border-dashed border-secondary' : 'border'} ${isIneligible ? 'bg-gray-50' : 'bg-white'} shadow w-full`}
    >
      {isEditMode && (
        <div className="absolute flex gap-0.5 -top-3 left-1/2 transform -translate-x-1/2 bg-secondary text-white px-2 py-1 rounded-full text-xs font-medium z-10">
          ÉDITION
          <button className="w-4 cursor-pointer" onClick={killEditMode}>
            <XMarkIcon className="w-full" />
          </button>
        </div>
      )}
      {
        contractFunnel.warningMessages &&
        <div className="left-2 absolute z-10">
          <FunnelCardWarning warningMessages={contractFunnel.warningMessages} />
        </div>
      }
      {/* Ligne principale : logo + garanties (grisés si inéligible) + actions */}
      <div className="flex w-full">
        <div className={`flex grow ${isIneligible ? 'opacity-40 grayscale pointer-events-none select-none' : ''}`}>
          <FunnelCardHeader
            logoURL={contractFunnel.product.logoURL}
            altLogo={contractFunnel.product && contractFunnel.product.owner && contractFunnel.product.owner.name}
            title={contractFunnel.product && contractFunnel.product.label}
          />
          <FunnelCardBody
            contractFunnel={contractFunnel}
            isProMode={isProMode}
          />
        </div>
        <FunnelCardFooter contractFunnel={contractFunnel} />
      </div>
      {/* Bandeau pleine largeur : motif d'inéligibilité (une puce par ligne, non tronqué) */}
      {isIneligible && contractFunnel.errorMessage && (
        <div className="flex items-start gap-2 rounded-b-xl border-t border-red-200 bg-red-50 px-4 py-2.5">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
          <p className="whitespace-pre-line text-xs leading-relaxed text-red-700">
            {contractFunnel.errorMessage}
          </p>
        </div>
      )}
    </div>
  );
};
